import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type {
  PublisherBillingStation,
  PublisherBillingStationMonth,
} from '../../../types/selfService';
import { formatCurrencyExact, formatImpressions } from '../metrics/metricsFormat';
import FeeInfoHover from './FeeInfoHover';
import { feeHoverCopy } from './feeHoverCopy';
import {
  blendedEcpmLabel,
  blendedRevShareLabel,
  bonusPercentLabel,
  cpmLabel,
  dayLabel,
  ecpmLabel,
  groupByMonth,
  monthLabel,
  revShareLabel,
  salesRepLabel,
  stationMonthFor,
  sortLedgerRows,
  sumLedgerRows,
  type LedgerRow,
  type LedgerSort,
  type LedgerSortKey,
  type LedgerTotals,
  type MonthGroup,
} from './payoutLedgerMath';

// CTV-776 / ADR-0037: the publisher's payout ledger. The same settlement
// figures the admin Streamline tab shows, in publisher vocabulary (CONTEXT.md
// "Billing tab (publisher)"). CTV-780 fixed the columns to read as the
// calculation left to right; CTV-786 made the advertiser the row heading (the
// campaign name its only subtext), moved the bonus percent under the eCPM,
// added the Sales rep column, centered the headers and put the blended eCPM
// and rev share on every totals row. Rows are grouped by DELIVERY month,
// because that is the unit a transfer pays; one row per campaign; a month
// subtotal under each group carries the ONE payment status for the
// station-month payout batch (ADR-0044). The deductions shown are the CHARGED
// figures, which is the arithmetic the publisher reconciles against.

interface PayoutLedgerProps {
  stations: PublisherBillingStation[];
  /** True under All stations: rows are grouped by station first, with a totals band above. */
  grouped: boolean;
  /**
   * The one delivery month to show ('YYYY-MM'). The month selector on the
   * page owns it; the ledger renders that month's rows and its subtotal, with
   * no collapsible group header (there is only one group).
   */
  month: string;
  loading?: boolean;
  error?: string | null;
}

/** The station-month payout batch chip (ADR-0044): Due > Expected > Paid, Nothing due. */
export const BatchStatusChip: React.FC<{ batch: PublisherBillingStationMonth | null }> = ({ batch }) => {
  if (!batch) return null;
  if (batch.display_status === 'paid') {
    return (
      <span className="inline-flex items-center gap-2 flex-wrap" data-testid="batch-status">
        <span className="inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800">
          Paid{batch.settled_at ? ` ${dayLabel(batch.settled_at)}` : ''}
        </span>
        {batch.stripe_transfer_id && (
          <span className="font-mono text-[11px] text-slate-500" data-testid="batch-transfer">{batch.stripe_transfer_id}</span>
        )}
      </span>
    );
  }
  if (batch.display_status === 'due_now') {
    return (
      <span className="inline-flex items-center gap-2 flex-wrap" data-testid="batch-status">
        <span className="inline-flex items-center rounded bg-amber-500 px-1.5 py-0.5 text-[11px] font-semibold text-white">Due</span>
        <span className="text-[11px] text-slate-500">payable since {dayLabel(batch.payable_at)}</span>
      </span>
    );
  }
  if (batch.display_status === 'nothing_due') {
    return (
      <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500" data-testid="batch-status">
        Nothing due
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600" data-testid="batch-status">
      Expected {dayLabel(batch.payable_at)}
    </span>
  );
};

// Twelve columns have to fit a laptop: headers may wrap onto two lines
// (leading-tight), cells stay compact. Every header is centered (CTV-786).
const HEAD = 'px-1 py-2 text-[10px] leading-tight font-medium uppercase tracking-[0.06em] text-slate-500 align-bottom text-center';
const NUM = 'px-1 py-2.5 text-right tabular-nums align-top text-[13px]';
const zeroOr = (cents: number) =>
  cents === 0 ? <span className="text-slate-400">$0.00</span> : formatCurrencyExact(cents);

/** One campaign row: the fixed CTV-786 columns, Station first under All stations. */
const CampaignRow: React.FC<{ row: LedgerRow; showStation?: boolean }> = ({
  row: { campaign, month, station },
  showStation,
}) => {
  const ecpm = ecpmLabel(campaign);
  const bonus = bonusPercentLabel(campaign);
  const rep = salesRepLabel(campaign);
  const refunded = campaign.status === 'payment_refunded';
  // The advertiser is the heading and the campaign name the subtext; a
  // campaign without an advertiser on file shows its name as the heading.
  const heading = campaign.advertiser_name || campaign.campaign_name;
  const sub = campaign.advertiser_name ? campaign.campaign_name : null;
  return (
    <tr
      className="border-t border-slate-100"
      data-testid="ledger-row"
      data-submission-id={campaign.submission_id}
      data-month={month.month}
      data-station-id={station.station_id}
    >
      {showStation && (
        <td className="px-3 py-2.5 align-top text-[13px] text-slate-700" data-testid="station-cell">
          {station.display_name || station.name}
        </td>
      )}
      <td className="px-3 py-2.5 align-top" data-testid="advertiser-cell">
        <div className="text-slate-900 font-medium text-[13px] leading-snug">{heading}</div>
        {sub && <div className="text-[12px] text-slate-500">{sub}</div>}
      </td>
      <td className={`${NUM} text-slate-700`} data-testid="delivered-cell">{formatImpressions(month.delivered_impressions)}</td>
      <td className={`${NUM} text-slate-700`} data-testid="invoiced-cell">{formatImpressions(month.invoiced_impressions)}</td>
      <td className={`${NUM} text-slate-700`} data-testid="ecpm-cell">
        {ecpm ? (
          <>
            <div>{ecpm}</div>
            {bonus && <div className="text-[11px] text-violet-700 whitespace-nowrap">{bonus}</div>}
          </>
        ) : (
          <span className="text-slate-400" title="No impression goal is recorded for this campaign, so there is no effective CPM to show.">
            n/a
          </span>
        )}
      </td>
      <td className={`${NUM} text-slate-900`} data-testid="gross-revenue-cell">{formatCurrencyExact(month.gross_revenue_cents)}</td>
      <td className={`${NUM} text-slate-600`} data-testid="rev-share-cell">{revShareLabel(campaign)}</td>
      <td className={`${NUM} text-slate-900 font-medium`} data-testid="net-revenue-cell">{formatCurrencyExact(month.net_revenue_cents)}</td>
      <td className={`${NUM} text-slate-700`} data-testid="waymark-cell">
        {campaign.waymark_exempt ? (
          month.waymark_impressions > 0 ? (
            <div title="You have your own Waymark agreement; Waymark bills these impressions to you directly.">
              <div className="text-slate-400">$0.00</div>
              <div className="text-[11px] text-slate-500 whitespace-nowrap">
                {formatImpressions(month.waymark_impressions)} impressions · $0
              </div>
            </div>
          ) : (
            <span className="text-slate-400">$0.00</span>
          )
        ) : month.waymark_impressions > 0 ? (
          <>
            <div>{formatCurrencyExact(month.waymark_deduction_cents)}</div>
            <div className="text-[11px] text-slate-500 whitespace-nowrap">
              {formatImpressions(month.waymark_impressions)} ×{' '}
              {cpmLabel((month.waymark_deduction_cents / month.waymark_impressions) * 10)} CPM
            </div>
          </>
        ) : (
          <span className="text-slate-400">$0.00</span>
        )}
      </td>
      <td className={`${NUM} text-slate-700`}>{zeroOr(month.ad_serving_deduction_cents)}</td>
      <td className={`${NUM} text-slate-700`}>{zeroOr(month.stripe_fee_cents)}</td>
      <td className={NUM} data-testid="payout-cell">
        <div className={`font-semibold ${month.net_payout_cents === 0 ? 'text-slate-500' : 'text-emerald-700'}`}>
          {formatCurrencyExact(month.net_payout_cents)}
        </div>
        {/* The one row note that survives CTV-786: a refunded campaign's $0
            needs a word, and it belongs with the figure it explains. */}
        {refunded && (
          <div className="text-[11px] text-slate-500" data-testid="refunded-note">Refunded</div>
        )}
      </td>
      <td className="px-2 py-2.5 pr-3 align-top text-[12px] text-slate-700 whitespace-nowrap" data-testid="sales-rep-cell">
        {rep}
      </td>
    </tr>
  );
};

/**
 * The numeric cells of a totals row (subtotal or the all-stations band).
 * `salesRep` false drops the trailing spacer for tables without that column
 * (the all-stations band: a commission belongs to one campaign's rep, so
 * summing the column across stations would mean nothing).
 */
const TotalsCells: React.FC<{ totals: LedgerTotals; emphasis?: boolean; salesRep?: boolean }> = ({ totals, emphasis, salesRep = true }) => {
  const cell = `px-1 py-2 text-right tabular-nums ${emphasis ? 'font-medium' : ''}`;
  return (
    <>
      <td className={cell}>{formatImpressions(totals.delivered)}</td>
      <td className={cell}>{formatImpressions(totals.invoiced)}</td>
      {/* CTV-786: the blended rates over the rows summed (gross ÷ invoiced ×
          1,000 and 1 − net ÷ gross), blank when there is nothing to divide by. */}
      <td className={cell} data-testid="totals-ecpm">{blendedEcpmLabel(totals)}</td>
      <td className={`${cell} font-medium`}>{formatCurrencyExact(totals.grossRevenue)}</td>
      <td className={cell} data-testid="totals-rev-share">{blendedRevShareLabel(totals)}</td>
      <td className={`${cell} font-medium`}>{formatCurrencyExact(totals.netRevenue)}</td>
      <td className={cell}>{formatCurrencyExact(totals.waymark)}</td>
      <td className={cell}>{formatCurrencyExact(totals.adServing)}</td>
      <td className={cell}>{formatCurrencyExact(totals.fee)}</td>
      <td className={`${cell} font-semibold text-emerald-700`}>{formatCurrencyExact(totals.payout)}</td>
      {salesRep && <td className="pr-3" />}
    </>
  );
};

/**
 * One station's month: its campaign rows and the month subtotal, which carries
 * the station-month payment status (ADR-0044). Single-station scope only; All
 * stations is a flat table with one grand total (CTV-786).
 */
const LedgerRows: React.FC<{
  group: MonthGroup;
  batch: PublisherBillingStationMonth | null;
}> = ({ group, batch }) => (
  <>
    {group.rows.map((row) => (
      <CampaignRow key={`${row.campaign.submission_id}-${row.month.month}`} row={row} />
    ))}
    <tr className="border-t border-slate-200 bg-slate-50/70 text-[12px] text-slate-600" data-testid="month-subtotal">
      <td className="px-3 py-2">
        <div className="flex flex-col items-start gap-1">
          <span className="italic whitespace-nowrap">
            {monthLabel(group.month)} total · {group.rows.length}{' '}
            {group.rows.length === 1 ? 'campaign' : 'campaigns'}
          </span>
          {/* ONE payment status per station-month (ADR-0044), not per campaign. */}
          <BatchStatusChip batch={batch} />
        </div>
      </td>
      <TotalsCells totals={group.totals} />
    </tr>
  </>
);

/**
 * The fee headers' hover copy depends on the rows in the table: an
 * organization with its own Waymark agreement sees the $0 variant. Rates are
 * the organization's own; with mixed rates in one table (never the case for
 * one station) the first row's stand.
 */
function hoverVariants(rows: LedgerRow[]) {
  const campaigns = rows.map((r) => r.campaign);
  const first = campaigns[0];
  return {
    waymarkRate: first?.waymark_cpm ?? 3,
    adServingRate: first?.ad_serving_cpm ?? 1.25,
    ownWaymark: campaigns.length > 0 && campaigns.every((c) => c.waymark_exempt),
  };
}

/** The sortable columns, in render order. `info` names the hover copy, if any. */
const COLUMNS: Array<{ key: LedgerSortKey; label: string; info?: 'ecpm' | 'waymark' | 'adServing' | 'stripe' }> = [
  { key: 'advertiser', label: 'Advertiser' },
  { key: 'delivered', label: 'Delivered impressions' },
  { key: 'invoiced', label: 'Invoiced impressions' },
  { key: 'ecpm', label: 'eCPM', info: 'ecpm' },
  { key: 'gross', label: 'Gross revenue' },
  { key: 'revShare', label: 'Rev share' },
  { key: 'net', label: 'Net revenue' },
  { key: 'waymark', label: 'Waymark', info: 'waymark' },
  { key: 'adServing', label: 'Ad serving', info: 'adServing' },
  { key: 'fee', label: 'Stripe fees', info: 'stripe' },
  { key: 'payout', label: 'Payout' },
  { key: 'rep', label: 'Sales rep' },
];

/**
 * The one header row both tables share (CTV-786): centered, fee hovers on the
 * three fee columns and the formula on eCPM, every column sortable. A column's
 * label is the sort control; a hover glyph sits beside it as its own button, so
 * reading the explanation never re-sorts the table.
 */
const HeaderRow: React.FC<{
  rows: LedgerRow[];
  salesRep?: boolean;
  showStation?: boolean;
  sort: LedgerSort | null;
  onSort: (key: LedgerSortKey) => void;
}> = ({ rows, salesRep = true, showStation, sort, onSort }) => {
  const v = hoverVariants(rows);
  const content = {
    ecpm: feeHoverCopy.ecpm,
    waymark: feeHoverCopy.waymark(v.waymarkRate, v.ownWaymark),
    adServing: feeHoverCopy.adServing(v.adServingRate),
    stripe: feeHoverCopy.stripe,
  };
  const cell = (
    key: LedgerSortKey,
    label: string,
    edge: string,
    info?: 'ecpm' | 'waymark' | 'adServing' | 'stripe',
  ) => {
    const active = sort?.key === key;
    return (
      <th
        key={key}
        className={`${HEAD} ${edge}`}
        aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
          <button
            type="button"
            onClick={() => onSort(key)}
            className="inline-flex items-center gap-0.5 uppercase hover:text-slate-700 focus:text-slate-700 focus:outline-none"
            data-testid={`sort-${key}`}
          >
            {label}
            {active &&
              (sort?.dir === 'asc' ? (
                <ChevronUp className="w-3 h-3 shrink-0" aria-hidden="true" />
              ) : (
                <ChevronDown className="w-3 h-3 shrink-0" aria-hidden="true" />
              ))}
          </button>
          {info && (
            <FeeInfoHover
              label={label}
              labelHidden
              content={content[info]}
              testId={`${info === 'stripe' ? 'stripe-fees' : info === 'adServing' ? 'ad-serving' : info}-info`}
            />
          )}
        </span>
      </th>
    );
  };
  return (
    <tr className="bg-slate-50/60 border-b border-slate-200">
      {showStation && cell('station', 'Station', 'pl-3 w-[9rem] min-w-[8rem]')}
      {COLUMNS.filter((c) => salesRep || c.key !== 'rep').map((c, i, list) =>
        cell(
          c.key,
          c.label,
          `${!showStation && i === 0 ? 'pl-3 ' : ''}${i === list.length - 1 ? 'pr-3 ' : ''}${c.key === 'advertiser' ? 'w-[13rem] min-w-[12rem]' : ''}`,
          c.info,
        ),
      )}
    </tr>
  );
};

const LedgerTable: React.FC<{
  groups: MonthGroup[];
  testId: string;
  batchFor: (month: string) => PublisherBillingStationMonth | null;
  sort: LedgerSort | null;
  onSort: (key: LedgerSortKey) => void;
}> = ({ groups, testId, batchFor, sort, onSort }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testId}>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <HeaderRow rows={groups.flatMap((g) => g.rows)} sort={sort} onSort={onSort} />
        </thead>
        <tbody>
          {groups.map((group) => (
            <LedgerRows
              key={group.month}
              group={{ ...group, rows: sortLedgerRows(group.rows, sort) }}
              batch={batchFor(group.month)}
            />
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/**
 * All stations in ONE table (CTV-786): a Station column on the left, every
 * station's campaigns in station order, a subtotal per station carrying that
 * station-month's payment status (ADR-0044: one status per station-month, and
 * that subtotal is the transfer amount the publisher reconciles), and the
 * all-stations total as the last row. Replaces the stack of one-table-per-
 * station under a separate totals band, and matches the CSV export's shape.
 */
const AllStationsTable: React.FC<{
  month: string;
  stations: PublisherBillingStation[];
  sort: LedgerSort | null;
  onSort: (key: LedgerSortKey) => void;
}> = ({ month, stations, sort, onSort }) => {
  // Flat: with no per-station subtotals left, the station is a column like any
  // other, so a sort can cross station boundaries freely. Unsorted, the rows
  // keep the server's station order (net revenue descending inside each).
  const natural = stations.flatMap(
    (station) => groupByMonth([station]).find((g) => g.month === month)?.rows ?? [],
  );
  const rows = sortLedgerRows(natural, sort);
  const total = sumLedgerRows(rows);
  const stationCount = new Set(rows.map((r) => r.station.station_id)).size;
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="ledger">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <HeaderRow rows={rows} showStation sort={sort} onSort={onSort} />
          </thead>
          <tbody>
            {rows.map((row) => (
              <CampaignRow
                key={`${row.station.station_id}-${row.campaign.submission_id}-${row.month.month}`}
                row={row}
                showStation
              />
            ))}
          </tbody>
          <tfoot>
            <tr
              className="border-t-2 border-slate-300 bg-slate-50 text-[12px] font-medium text-slate-700"
              data-testid="ledger-total"
            >
              <td className="px-3 py-2.5 font-semibold">All stations</td>
              <td className="px-3 py-2.5">
                {monthLabel(month)} · {stationCount} {stationCount === 1 ? 'station' : 'stations'} ·{' '}
                {rows.length} {rows.length === 1 ? 'campaign' : 'campaigns'}
              </td>
              <TotalsCells totals={total} emphasis />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

/** The month has no rows anywhere in scope: a different message when nothing has ever delivered. */
const EmptyMonth: React.FC<{ month: string; stations: PublisherBillingStation[] }> = ({ month, stations }) => {
  const anyRows = stations.some((st) => st.campaigns.some((c) => c.months.length > 0));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-600" data-testid="ledger-empty">
      <div className="font-medium text-slate-800">
        {anyRows ? `Nothing delivered in ${monthLabel(month)}.` : 'No paid campaigns have delivered yet.'}
      </div>
      <div className="mt-1 text-slate-500">
        {anyRows
          ? 'Pick another month above to see its payouts.'
          : "Once an advertiser's campaign runs, each month of delivery appears here with your net revenue, the deductions, and the payout date."}
      </div>
    </div>
  );
};

const PayoutLedger: React.FC<PayoutLedgerProps> = ({ stations, grouped, month, loading, error }) => {
  // CTV-786: click a column to sort, click it again to reverse. Null is the
  // server's own order, which is what the ledger opens on.
  const [sort, setSort] = React.useState<LedgerSort | null>(null);
  const onSort = (key: LedgerSortKey) =>
    setSort((prev) =>
      prev?.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );

  if (error) {
    return (
      <div role="alert" className="rounded-xl border border-red-200 bg-red-50 border-l-[3px] border-l-red-500 px-4 py-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-red-700">Could not load your billing</div>
        <div className="mt-1 text-sm text-red-800">{error}</div>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-3" data-testid="ledger-skeleton">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-4 rounded bg-slate-200 animate-pulse" style={{ width: `${90 - i * 12}%` }} />
        ))}
      </div>
    );
  }

  const groupsFor = (list: PublisherBillingStation[]) =>
    groupByMonth(list).filter((g) => g.month === month);

  if (grouped) {
    // CTV-786: one table for every station, not a stack of per-station tables
    // under a separate band. A station with nothing in the month is absent.
    const withRows = stations.filter((st) => groupsFor([st]).length > 0);
    if (withRows.length === 0) return <EmptyMonth month={month} stations={stations} />;
    return <AllStationsTable month={month} stations={withRows} sort={sort} onSort={onSort} />;
  }

  const groups = groupsFor(stations);
  if (groups.length === 0) return <EmptyMonth month={month} stations={stations} />;
  // Single-station scope: the one station's batch for the month.
  const station = stations[0];
  return (
    <LedgerTable
      groups={groups}
      testId="ledger"
      batchFor={(m) => (station ? stationMonthFor(station, m) : null)}
      sort={sort}
      onSort={onSort}
    />
  );
};

export default PayoutLedger;
